import Head from "next/head";
import React from "react";

export default function TermsPage() {
  return (
    <>
      <Head>
        <title>Terms of Service | predraft.ai</title>
        <link rel="canonical" href="https://predraft.ai/terms" key="canonical" />
      </Head>
      <h1>Terms of Service</h1>
      <h2>Last Updated: July 13, 2023</h2>
      <h3>Acceptance of Terms</h3>
      <p>
        These Terms of Service (&quot;Terms&quot;) govern your use of the Predraft.ai website
        (&quot;Website&quot;, &quot;Service&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;).
        By using our Website, you agree to these Terms in full. If you disagree with these Terms or
        any part of these Terms, you must not use our Website.
      </p>
      <h3>User Conduct</h3>
      <p>
        You are responsible for all content that you generate or transmit through the Website. You
        must not use our Website in any way that causes, or may cause, damage to the Website or
        impairment of the availability or accessibility of the Website, or in any way which is
        unlawful, illegal, fraudulent or harmful, or in connection with any unlawful, illegal,
        fraudulent or harmful purpose or activity.
      </p>
      <p>
        You hereby indemnify us and undertake to keep us indemnified against any losses, damages,
        costs, liabilities and expenses incurred or suffered by us arising out of any breach by you
        of any provision of these Terms.
      </p>
      <h3>Content Ownership</h3>
      <p>
        The content you generate on the Website remains your property; however, by generating
        content, you grant us a worldwide, irrevocable, non-exclusive, royalty-free license to use,
        reproduce, adapt, publish, translate and distribute your content in any existing or future
        media. You also grant us the right to sublicense these rights and the right to bring an
        action for infringement of these rights.
      </p>
      <h3>Copyright Infringement</h3>
      <p>
        We do not host the articles users generate, and we do not serve them. Takedown requests
        should be directed to OpenAI.
      </p>
      <h3>Business Transfers</h3>
      <p>
        In the event that Predraft.ai is involved in a bankruptcy, merger, acquisition,
        reorganization or sale of assets, your information may be sold or transferred as part of
        that transaction. This Terms will apply to your information as transferred to the new
        entity.
      </p>
      <h3>Payments and Refund Policy</h3>
      <p>
        All payments for our Services are final and non-refundable. Subscription cancellations will
        take effect at the end of the current billing period.
      </p>
      <h3>Limitations of Liability</h3>
      <p>
        To the maximum extent permitted by applicable law, we exclude all representations,
        warranties and conditions relating to our Website and the use of our Website. Nothing in
        these Terms will exclude or limit our liability for fraud, for death or personal injury
        caused by our negligence, or for any other liability which cannot be excluded or limited
        under applicable law.
      </p>
      <h3>Changes to the Terms</h3>
      <p>
        We may revise these Terms of Service at any time without notice. By using this Website you
        agree to be bound by the current version of these Terms of Service. You can always review
        the most current version of the Terms of Service at{" "}
        <a href="https://predraft.ai/terms" target="_new">
          https://predraft.ai/terms
        </a>
        .
      </p>
      <h3>Account Termination</h3>
      <p>
        You may terminate your account at any time by contacting our support team. We also reserve
        the right to terminate or suspend your account at any time for any or no reason, with or
        without notice.
      </p>
      <h3>Dispute Resolution</h3>
      <p>
        Any disputes arising out of or related to these Terms of Service or the Services will be
        governed by the laws of Delaware, without regard to its conflict of laws rules. Both parties
        consent to personal jurisdiction in Delaware and agree to resolve any disputes exclusively
        in state or federal courts located in Delaware. Disputes will be subject to arbitration
        under AAA rules.
      </p>
      <h3>Affiliate Program Terms and Conditions</h3>
      <p>
        By participating in the Predraft.ai Affiliate Program (&quot;Affiliate Program&quot;), you
        agree to the following terms and conditions in addition to the Terms of Service:
      </p>
      <p>
        1. Eligibility: To become an affiliate, you must be a registered user of the Predraft.ai
        website. Individuals and businesses are eligible to participate in the Affiliate Program,
        subject to our approval.
      </p>
      <p>
        2. Enrollment: To join the Affiliate Program, you must apply through the designated
        application process provided on the Predraft.ai website. We reserve the right to accept or
        reject any application for any reason.
      </p>
      <p>
        3. Affiliate Commissions: As an affiliate, you will earn commissions on qualifying sales
        made through your unique referral link. Commissions will be paid according to the commission
        structure outlined in the Affiliate Program documentation available on the Predraft.ai
        website.
      </p>
      <p>
        4. Commissions&apos; Eligibility: Commissions are eligible only for new customers who sign
        up for Predraft.ai services through your referral link and complete a qualifying purchase.
      </p>
      <p>
        5. Cookie Duration: The tracking cookie used for the Affiliate Program will be active for a
        limited duration, as specified in the Affiliate Program documentation. Commissions will be
        awarded only for customers who make a purchase within the cookie duration from the time of
        clicking on your referral link.
      </p>
      <p>
        6. Prohibited Activities: As an affiliate, you must not engage in any activity that may be
        harmful to the Predraft.ai brand or reputation. Prohibited activities include but are not
        limited to:
      </p>
      <p>a. Self-referral or creating multiple accounts to earn commissions.</p>
      <p>b. Misleading or deceptive promotion of Predraft.ai services.</p>
      <p>
        c. Promotion of Predraft.ai on websites or platforms containing illegal, offensive, or
        objectionable content.
      </p>
      <p>
        d. Bidding on trademarked terms or keywords related to Predraft.ai in search engine
        marketing campaigns without prior approval.
      </p>
      <p>
        7. Termination: We reserve the right to terminate your participation in the Affiliate
        Program at any time and for any reason, including but not limited to violations of these
        terms and conditions or the Terms of Service. Upon termination, any outstanding commissions
        will be forfeited.
      </p>
      <p>
        8. Commission Payments: Commissions will be paid out according to the payment schedule and
        method specified in the Affiliate Program documentation. Minimum payment thresholds may
        apply.
      </p>
      <p>
        9. Modification of Terms: We may modify these Affiliate Program terms at any time without
        notice. Continued participation in the Affiliate Program after any changes will constitute
        your acceptance of the modified terms.
      </p>
      <p>
        10. Independent Contractor: You understand and agree that your participation in the
        Affiliate Program does not create an employment, partnership, or agency relationship with
        Predraft.ai. You are an independent contractor responsible for your own taxes and legal
        compliance.
      </p>
      <p>
        11. Entire Agreement: These Affiliate Program terms, together with the Terms of Service,
        constitute the entire agreement between you and Predraft.ai regarding your participation in
        the Affiliate Program.
      </p>
      <p>
        If you have any questions or need further clarification about the Affiliate Program terms
        and conditions, please contact us at affiliates@predraft.ai.
      </p>
      <p>Effective as of July 13, 2023</p>
      <h3>Contact Information</h3>
      <p>
        If you have any questions about these Terms, please contact us at{" "}
        <a href="mailto:hello@predraft.ai" target="_new">
          hello@predraft.ai
        </a>
      </p>
      <h3>Effective as of July 13, 2023</h3>
    </>
  );
}
